import type { ComposioToolkitId } from "@/lib/integrations/catalog";
import type { TriggerPatch } from "@/lib/worker/workerConfig";

/** Blueprint id for the auto-provisioned knowledge-base auditor worker. */
export const AUDITOR_BLUEPRINT_ID = "knowledge-base-auditor";

/** Pre-built worker template for 1-click setup in the UI or via Smithers. */
export interface WorkerBlueprint {
  id: string;
  title: string;
  description: string;
  /** Suggested department (folder) name, e.g. "Customer Support". */
  department: string;
  /** provider/model-id recommendation for this use case. */
  suggestedModel: string;
  instructions: string;
  defaultTriggers: TriggerPatch[];
  recommendedToolkits: ComposioToolkitId[];
}

const GMAIL_INBOX_POLL_CONFIG = JSON.stringify({
  labelIds: ["INBOX"],
  unreadOnly: true,
  maxPerPoll: 15,
});

const GMAIL_FINANCE_POLL_CONFIG = JSON.stringify({
  labelIds: ["INBOX"],
  unreadOnly: false,
  maxPerPoll: 20,
});

export const WORKER_BLUEPRINTS: WorkerBlueprint[] = [
  {
    id: "customer-support-triage",
    title: "Customer Support Triage",
    description:
      "Monitors your Gmail inbox, assigns priority labels, and drafts reply suggestions for your team to review.",
    department: "Customer Support",
    suggestedModel: "anthropic/claude-sonnet-4-5",
    instructions: [
      "You are the Customer Support Triage agent for this business.",
      "",
      "When new inbox messages arrive:",
      "1. Read each message and classify urgency: P0 (outage/security), P1 (blocked customer), P2 (standard question), P3 (FYI/no reply needed).",
      "2. Apply or recommend Gmail labels that match priority (create consistent label names if missing).",
      "3. For P0–P2, draft a concise, empathetic reply in the customer's language. Do not send — save as a draft for human review.",
      "4. Summarize what you triaged in a short status note: sender, subject, priority, action taken.",
      "",
      "Rules:",
      "- Never send email on behalf of the team without explicit approval in instructions.",
      "- Escalate billing disputes and legal threats to P1 even if the tone is calm.",
      "- If Gmail is not connected, file a resource request explaining what access is needed.",
      "- Log recurring themes (shipping delays, login issues) to the knowledge base when patterns emerge.",
    ].join("\n"),
    defaultTriggers: [
      {
        type: "poll",
        cronExpression: "*/15 * * * *",
        source: "gmail",
        configJson: GMAIL_INBOX_POLL_CONFIG,
        prompt:
          "Poll Gmail for new inbox messages. Triage each new thread: classify priority, label, draft replies where needed, and post a brief summary of actions.",
        enabled: true,
      },
    ],
    recommendedToolkits: ["gmail", "slack"],
  },
  {
    id: "daily-standup-ops-summarizer",
    title: "Daily Standup & Ops Summarizer",
    description:
      "Pulls task and channel updates each morning and synthesizes a daily operations brief for the team.",
    department: "Operations",
    suggestedModel: "openai/gpt-4o",
    instructions: [
      "You are the Daily Standup & Ops Summarizer for this business.",
      "",
      "Each run, produce a concise daily brief with these sections:",
      "1. **Yesterday / since last run** — completed work, merged PRs, closed tickets, notable Slack threads.",
      "2. **In progress** — active tasks, owners, and blockers.",
      "3. **Today** — suggested focus items and any meetings or deadlines.",
      "4. **Risks & asks** — anything that needs a decision or human follow-up.",
      "",
      "Sources to check (use what's connected): task boards, Slack channels the team uses for standups, and the knowledge base for standing priorities.",
      "Keep the brief scannable: bullets, names, links where available. Target length: one screen.",
      "If a source is unavailable, say so briefly and continue with what you have.",
      "Append notable updates to a KB page named `ops/daily-brief` (create if missing) with the date as a heading.",
    ].join("\n"),
    defaultTriggers: [
      {
        type: "cron",
        cronExpression: "0 9 * * 1-5",
        prompt:
          "Generate today's standup and operations brief. Pull updates from connected task and chat tools, then write the structured daily summary.",
        enabled: true,
      },
    ],
    recommendedToolkits: ["slack", "linear", "notion"],
  },
  {
    id: "invoice-expense-extractor",
    title: "Invoice & Expense Extractor",
    description:
      "Scans email for invoices and receipts, extracts amounts and vendors, and logs structured entries to the knowledge base.",
    department: "Finance",
    suggestedModel: "google/gemini-3.6-flash",
    instructions: [
      "You are the Invoice & Expense Extractor for this business.",
      "",
      "When processing email (or attachments referenced in email):",
      "1. Identify messages that contain invoices, receipts, or payment confirmations.",
      "2. Extract: vendor name, invoice/receipt number (if present), date, currency, subtotal, tax, total, and payment status.",
      "3. Skip duplicates — check the knowledge base log before adding the same invoice number twice.",
      "4. Append a structured markdown row to KB page `finance/expenses` with: date, vendor, amount, category (guess if unclear), source message subject, and notes.",
      "5. Post a one-line summary of new entries found this run.",
      "",
      "Rules:",
      "- Do not modify accounting systems unless explicitly connected and instructed.",
      "- Flag amounts over an internal threshold (default $5,000) for manual review.",
      "- If PDF attachments cannot be read, note the gap and request access or a forward with plain-text details.",
    ].join("\n"),
    defaultTriggers: [
      {
        type: "poll",
        cronExpression: "0 */2 * * *",
        source: "gmail",
        configJson: GMAIL_FINANCE_POLL_CONFIG,
        prompt:
          "Poll Gmail for new messages likely containing invoices or receipts. Parse amounts and log structured entries to the finance/expenses knowledge-base page.",
        enabled: true,
      },
    ],
    recommendedToolkits: ["gmail", "googledrive"],
  },
  {
    id: "lead-crm-researcher",
    title: "Lead & CRM Researcher",
    description:
      "Enriches inbound leads from webhooks with public research and saves intelligence to the knowledge base.",
    department: "Sales",
    suggestedModel: "anthropic/claude-sonnet-4-5",
    instructions: [
      "You are the Lead & CRM Researcher for this business.",
      "",
      "When a lead payload arrives via webhook:",
      "1. Parse contact fields: name, email, company, title, message, and any UTM or source metadata.",
      "2. Research the person and company using available tools and the knowledge base (prior interactions, existing customers).",
      "3. Produce a lead brief: company size/industry guess, likely pain points, fit score (1–5), recommended next step, and talking points.",
      "4. Save or update a KB page under `leads/<company-slug>` with the full brief and raw payload reference.",
      "5. If the lead matches an existing account, link to that KB page and note the relationship.",
      "",
      "Rules:",
      "- Do not email or message the lead directly — research and routing only.",
      "- Respect privacy: store only business-relevant facts, not speculative personal details.",
      "- If the payload is incomplete, document what's missing and still save partial research.",
    ].join("\n"),
    defaultTriggers: [
      {
        type: "webhook",
        prompt:
          "A new inbound lead arrived via webhook. Enrich with company and contact research, score fit, and save the intelligence brief to the knowledge base.",
        enabled: true,
      },
    ],
    recommendedToolkits: ["slack", "notion"],
  },
  {
    id: AUDITOR_BLUEPRINT_ID,
    title: "Knowledge Base Auditor",
    description:
      "Weekly review of index.md and core wiki pages — finds gaps, contradictions, and stale facts, then asks you to clarify and updates the KB.",
    department: "Operations",
    suggestedModel: "anthropic/claude-sonnet-4-5",
    instructions: [
      "You are the Knowledge Base Auditor for this business.",
      "",
      "Each run:",
      "1. Read index.md with kb_read, then read the core wiki pages it links to (customers, pricing, products, policies, and other standing topics).",
      "2. Look for missing information, contradictions between pages, and facts that appear out of date.",
      "3. When you find a gap you cannot resolve from existing pages, use request_resource with kind \"clarification\" — one request per distinct question, with a clear title and the context you already have.",
      "4. After the user replies to a clarification (check open and recently resolved resource requests), integrate their answer with kb_update on the affected pages and refresh index.md links or summaries as needed.",
      "5. Post a brief status update summarizing what you reviewed, what you fixed, and any open clarifications.",
      "",
      "Rules:",
      "- Do not invent facts — ask when uncertain.",
      "- Prefer updating existing pages over creating duplicates; note superseded claims.",
      "- Skip log.md; it is maintained automatically.",
      "- If the knowledge base is empty, file one clarification asking what to document first.",
    ].join("\n"),
    defaultTriggers: [
      {
        type: "cron",
        cronExpression: "0 9 * * 1",
        prompt:
          "Audit the knowledge base: read index.md and core wiki pages, flag gaps and contradictions, file clarification requests where needed, and update pages after user replies.",
        enabled: true,
      },
    ],
    recommendedToolkits: [],
  },
];

/** All production-ready worker blueprints. */
export function listWorkerBlueprints(): WorkerBlueprint[] {
  return WORKER_BLUEPRINTS;
}

/** Lookup a blueprint by id (case-sensitive). */
export function getWorkerBlueprint(id: string): WorkerBlueprint | undefined {
  return WORKER_BLUEPRINTS.find((b) => b.id === id);
}

/** Summary shape returned to Smithers and API consumers. */
export function summarizeBlueprint(b: WorkerBlueprint) {
  return {
    id: b.id,
    title: b.title,
    description: b.description,
    department: b.department,
    suggestedModel: b.suggestedModel,
    recommendedToolkits: b.recommendedToolkits,
    triggerTypes: b.defaultTriggers.map((t) => t.type),
  };
}

/** Match a blueprint department to an existing folder option (best effort). */
export function matchFolderIdForDepartment(
  department: string,
  folderOptions: { id: string; label: string }[]
): string {
  const target = department.trim().toLowerCase();
  if (!target) return "";

  for (const opt of folderOptions) {
    const stripped = opt.label.replace(/^[\s\u00A0└]+/, "").trim();
    if (stripped.toLowerCase() === target) return opt.id;
  }
  return "";
}

/** Values to pre-fill the new-worker form from a blueprint. */
export function blueprintFormPrefill(
  blueprint: WorkerBlueprint,
  folderOptions: { id: string; label: string }[] = []
) {
  return {
    name: blueprint.title,
    description: blueprint.description,
    instructions: blueprint.instructions,
    model: blueprint.suggestedModel,
    folderId: matchFolderIdForDepartment(blueprint.department, folderOptions),
    triggers: blueprint.defaultTriggers,
    recommendedToolkits: blueprint.recommendedToolkits,
  };
}
